/**
 * ColumnHeader
 *
 * Renders a header cell with sort indicator and a 4px drag handle for resize.
 *
 * @module components/common/DataTable/ColumnHeader
 */

import React from 'react';
import PropTypes from 'prop-types';

const sortIconClass = (state) => {
  if (state === 'asc') return 'bi bi-arrow-up ms-1';
  if (state === 'desc') return 'bi bi-arrow-down ms-1';
  return null;
};

const ColumnHeader = ({ header, enableResize }) => {
  const column = header.column;
  const sortable = column.getCanSort();
  const sortState = column.getIsSorted(); // 'asc' | 'desc' | false
  const align = column.columnDef.meta?.align || 'left';
  const resizable = enableResize && column.getCanResize();

  const handleSortClick = (evt) => {
    if (!sortable) return;
    // TanStack handles shift-click multi-sort automatically when enableMultiSort=true
    column.getToggleSortingHandler()?.(evt);
  };

  const icon = sortIconClass(sortState);

  const headerLabel = typeof column.columnDef.header === 'function'
    ? column.columnDef.header(header.getContext())
    : column.columnDef.header;

  return (
    <th
      style={{
        position: 'relative',
        width: header.getSize() ? `${header.getSize()}px` : undefined,
        textAlign: align,
        cursor: sortable ? 'pointer' : 'default',
        userSelect: 'none',
      }}
      className={sortable ? 'dt-th-sortable' : ''}
      onClick={sortable ? handleSortClick : undefined}
      aria-sort={sortState === 'asc' ? 'ascending' : sortState === 'desc' ? 'descending' : 'none'}
    >
      <span>{headerLabel}</span>
      {sortable && icon && <i className={icon} aria-hidden="true" />}
      {resizable && (
        <span
          className="dt-resize-handle"
          onMouseDown={(e) => {
            e.stopPropagation();
            header.getResizeHandler()(e);
          }}
          onTouchStart={(e) => {
            e.stopPropagation();
            header.getResizeHandler()(e);
          }}
          onClick={(e) => e.stopPropagation()}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize column"
        />
      )}
    </th>
  );
};

ColumnHeader.propTypes = {
  header: PropTypes.object.isRequired,
  enableResize: PropTypes.bool,
};

export default ColumnHeader;
